import { useEffect, useRef } from "react"
import { useQuery, type QueryKey, type QueryMeta } from "@tanstack/react-query"
import { useLocation } from "wouter"
import { paths } from "../../../paths"
import { isUnauthorized } from "@rawa/api-client/ApiResponseError"

type Options<T> = {
  queryKey: QueryKey
  queryFn: () => Promise<T>
  /** Called once for each failure, after the client's retries. Not for a 401. */
  onError?: (err: unknown) => void
  /** `source` names the failure in telemetry; see queryClient.ts for the rest. */
  meta?: QueryMeta
}

/**
 * Loads a kiosk page's data from a device-authenticated endpoint.
 *
 * A 401 means the device credential no longer resolves to a usable tablet —
 * cleared, evicted by the browser, or revoked (ADR-025). It returns the tablet to
 * setup rather than calling `onError`, so a client is never shown an authentication
 * failure they can do nothing about, and it is not logged as an exception.
 *
 * Everything else is the page's to handle, because the kiosk surfaces genuinely
 * differ: the session list reports the failure in place, while the participants
 * page navigates back to the list. Retries and reporting are the query client's
 * (ADR-031): a dropped connection is retried, a 4xx is an answer at once.
 *
 * A page only ever shows what it fetched itself. A session from an earlier visit
 * may have closed, or had its last participant answer, since — and a client
 * tapping it would hit a dead end — so a return visit shows loading, not the last
 * visit's list, and is not handed the last visit's failure either. Within a
 * visit, the client's defaults refetch when the tablet wakes and when its Wi-Fi
 * rejoins, keeping what is shown until the new answer lands.
 *
 * `onError` is held in a ref so a page can pass an inline callback without
 * re-running anything every render.
 */
export function useKioskQuery<T>({
  queryKey,
  queryFn,
  onError,
  meta,
}: Options<T>): { data: T | null; loading: boolean; failed: boolean } {
  const [, navigate] = useLocation()
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const { data, error, isError, isFetchedAfterMount } = useQuery({
    queryKey,
    queryFn,
    meta,
    refetchOnMount: "always",
  })

  useEffect(() => {
    if (!isFetchedAfterMount || !error) return
    if (isUnauthorized(error)) {
      navigate(paths.kioskLogin)
      return
    }
    onErrorRef.current?.(error)
  }, [error, isFetchedAfterMount, navigate])

  return {
    data: isFetchedAfterMount ? (data ?? null) : null,
    loading: !isFetchedAfterMount,
    failed: isFetchedAfterMount && isError && !isUnauthorized(error),
  }
}
